// 9 marketing capabilities — CMV Vision tone: confident, momentum-driven, growth-led.
const services = [
  {
    slug: "brand-strategy",
    title: "Brand Strategy",
    blurb: "Positioning, messaging architecture and the strategic spine the rest of your marketing actually sits on.",
    body: [
      "Most marketing problems read as channel problems but are actually strategy problems — the brand has no point of view, the positioning is unclear, or the messaging architecture contradicts itself across the funnel. We start by fixing that.",
      "Each engagement opens with a structured strategic sprint: category audit, competitor mapping, customer interviews and the internal stakeholder alignment that prevents the same fight from happening every quarter. The output is a written strategy your CFO, your CMO and your agency partners can all defend.",
      "Strategy that does not survive contact with execution is just a deck. We stay with the engagement through the first three campaigns so the strategy actually shapes the work being shipped."
    ],
    covers: [
      "Category, competitor and audience research",
      "Brand positioning and messaging architecture",
      "Customer journey mapping and trigger analysis",
      "Internal alignment workshops with leadership",
      "Strategic playbook that briefs every downstream channel"
    ]
  },
  {
    slug: "performance-media",
    title: "Performance Media",
    blurb: "Paid search, paid social and programmatic — built around incremental contribution, not last-click vanity.",
    body: [
      "Performance media is bought with the same money as any other line item. We treat it that way: incrementality testing, media-mix discipline and reporting against the business metric that actually matters — not the platform metric that flatters the agency.",
      "We run paid search, paid social, programmatic display, retail media and connected TV as a single managed practice. Creative is produced in volume, in the languages your markets actually consume, with a velocity the platforms reward.",
      "Monthly reports are written for the CMO and CFO, not the optimisation specialist. Every dollar is accounted for, every lift is verified, and every test has a hypothesis the team can defend in writing."
    ],
    covers: [
      "Channel mix planning with sized investment per platform",
      "Creative production cadence localised per market",
      "Bidding, audience and exclusion management",
      "Incrementality and media-mix modelling",
      "Monthly business-focused reporting in CFO language"
    ]
  },
  {
    slug: "content-seo",
    title: "Content & SEO",
    blurb: "Topical authority programmes — keyword research to long-form to technical SEO to authority building.",
    body: [
      "SEO is the only marketing channel that compounds. We run organic search as a long-form authority programme: keyword research grounded in commercial intent, content roadmaps tied to revenue, and technical hygiene that keeps the foundation strong as the surface area grows.",
      "Writers are local-language editors with category expertise — not generic copy mills. Long-form pieces ship monthly under named contributors. Technical SEO is monitored continuously with documented Core Web Vitals targets.",
      "Reporting focuses on share-of-voice against named competitors and revenue contribution from organic — not vanity rank reports for keywords no one searches."
    ],
    covers: [
      "Keyword research with commercial intent and search-volume sizing",
      "Editorial roadmap and content production by local editors",
      "Technical SEO audit, Core Web Vitals and structured data",
      "Authority building through earned placements and digital PR",
      "Quarterly SOV reporting against named category leaders"
    ]
  },
  {
    slug: "social-creator",
    title: "Social & Creator",
    blurb: "Daily-operated social channels plus contracted creator partnerships — measured against the business, not the feed.",
    body: [
      "Social done occasionally signals an absent brand. We run social as a managed daily service across Instagram, TikTok, LinkedIn, YouTube and X — with content production, publishing, community management and the crisis protocols rehearsed before they are needed.",
      "Creator strategy sits alongside paid social: vetted partners, contracted with usage rights and exclusivity windows, measured against incremental business contribution rather than reach inflation.",
      "Brand voice and tone is codified into a written guide that the social team, the creators and any in-house contributor can reference — keeping the brand consistent regardless of who is posting on a given day."
    ],
    covers: [
      "Channel strategy and monthly editorial planning",
      "Content production: photo, motion and short-form video",
      "Community management with response-time SLAs",
      "Creator long-listing, contracting and campaign management",
      "Crisis-response protocol with rehearsed escalation paths"
    ]
  },
  {
    slug: "design-systems",
    title: "Design & Visual Identity",
    blurb: "Brand systems, identity, design language and the working files that keep everyone on-brand.",
    body: [
      "Identity is not a logo, it is a system. We build brand and design systems that survive scale — across every channel, every market, every internal team that has ever opened InDesign with the best intentions and the wrong file.",
      "Engagements range from full identity programmes (naming, mark, type system, colour, motion, application) to refresh work for established brands that need to step up without losing equity. Every system ships with working files, usage rules and a brand portal team can self-serve from.",
      "We work alongside in-house designers as a guide, not a replacement — so the system continues to live after the engagement ends."
    ],
    covers: [
      "Logo, wordmark and visual identity system",
      "Type system, colour, motion and tone of voice",
      "Brand book, usage rules and accessible portal",
      "Application templates: digital, print, social, retail",
      "Internal handover with working files and training"
    ]
  },
  {
    slug: "campaigns",
    title: "Campaign Creative",
    blurb: "Integrated campaigns — concept to production to delivery — for moments that matter.",
    body: [
      "Campaigns are how a brand earns the right to be remembered. We concept, produce and ship integrated work across film, photography, social, OOH and digital — for product launches, brand moments and the seasonal beats that actually move a category.",
      "Engagements are scoped against the moment. A teaser plus launch sequence for a new product. A regional brand campaign across three markets. A cultural-moment activation that ships in two weeks. Every campaign closes with measurement against the goals we set at brief.",
      "Production happens with vetted local crews and post-production partners in each market we run in — keeping cost realistic and cultural translation honest."
    ],
    covers: [
      "Strategic brief shaping and creative concepting",
      "Film, photography and motion production across markets",
      "Integrated channel rollout: paid, organic, owned",
      "OOH, print and experiential extension where relevant",
      "Post-launch measurement and learning capture"
    ]
  },
  {
    slug: "growth-experiments",
    title: "Growth Experiments",
    blurb: "Structured testing — landing pages, onboarding, lifecycle — to find the lift before scaling spend.",
    body: [
      "Performance media buys traffic. Growth experiments figure out what to do with it. We run a structured test-and-learn programme across landing pages, signup flows, onboarding, lifecycle messaging and pricing pages — making every dollar of acquisition work harder by the time it lands.",
      "Hypotheses are written in advance. Tests have power calculations. Results are read against incremental lift, not before-and-after observation. The wins ship; the losses go into the learning log.",
      "Most clients are surprised how much of their growth lives inside the funnel they already paid to build."
    ],
    covers: [
      "Hypothesis backlog and prioritisation framework",
      "Landing page, signup and checkout experimentation",
      "Onboarding flow and activation testing",
      "Lifecycle e-mail, in-app and CRM experimentation",
      "Monthly experiment readouts with verified lift"
    ]
  },
  {
    slug: "analytics",
    title: "Analytics & Attribution",
    blurb: "Server-side tagging, attribution and the boring dashboards that make resource decisions defensible.",
    body: [
      "Marketing decisions get made on the numbers leadership trusts. We build the measurement stack that makes those numbers trustworthy: GA4 implemented correctly, server-side tagging, consent management aligned with local regulation, and channel reporting reconciled against a single source of truth.",
      "Where it matters, we layer media-mix modelling and lift testing so executive decisions are made on real evidence rather than platform-attributed self-reports.",
      "Dashboards stay boring on purpose. The point is decisions, not pixels."
    ],
    covers: [
      "GA4, GTM and server-side tagging build and audit",
      "Consent management aligned with GDPR / KVKK",
      "Single source of truth for marketing, sales and finance",
      "Media-mix modelling and quarterly incrementality testing",
      "Executive reporting in business language, not platform jargon"
    ]
  },
  {
    slug: "pr-comms",
    title: "PR & Communications",
    blurb: "Earned media, thought leadership and the crisis playbook you hope you never have to run.",
    body: [
      "Earned coverage moves audiences advertising can not buy. We place stories in titles that actually move your category — not coverage volume metrics for proposals.",
      "Engagements include product launches, executive thought leadership, regional brand campaigns and the always-on relationship work that keeps your brand in the right conversations. Crisis communications, when needed, is rehearsed and on-call.",
      "We measure earned by quality, brand search lift, and contribution to pipeline — not press clip counts."
    ],
    covers: [
      "Annual PR strategy and messaging architecture",
      "Press releases, journalist relations and story placement",
      "Executive thought leadership and ghostwritten contributions",
      "Press events and product launch activation",
      "Crisis communications planning and on-call response"
    ]
  }
];

export default services;
export const getService = (slug) => services.find((s) => s.slug === slug) || null;
